import type { ApplePayConfiguration } from '@adyen/adyen-web';
import type { AdyenRecurringPaymentRequestModel, TimeUnitModel } from '../../../common/src';
import { checkIfDateIsValid } from '@/shared/date-utils';

const recurringIntervals: Partial<Record<TimeUnitModel, { unit: ApplePayJS.ApplePayRecurringPaymentDateUnit; count: number }>> = {
  YEAR: { unit: 'year', count: 1 },
  MONTH: { unit: 'month', count: 1 },
  WEEK: { unit: 'day', count: 7 },
  DAY: { unit: 'day', count: 1 },
  HOUR: { unit: 'hour', count: 1 },
  MINUTE: { unit: 'minute', count: 1 },
};

/** A redirect query can be edited or bookmarked, so reject invalid optional sheet details. */
export function parseAdyenRecurringPaymentRequest(value: unknown): AdyenRecurringPaymentRequestModel | null {
  if (typeof value !== 'string') return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const request = parsed as Record<string, unknown>;
    if (request.planName !== undefined && (typeof request.planName !== 'string' || !request.planName.trim())) return null;
    if (typeof request.amount !== 'number' || !Number.isFinite(request.amount) || request.amount < 0) return null;
    if (typeof request.currency !== 'string' || !/^[A-Z]{3}$/.test(request.currency)) return null;
    if (request.startDate !== undefined && (typeof request.startDate !== 'string' || !checkIfDateIsValid(request.startDate))) {
      return null;
    }
    if (
      request.periodUnit !== undefined &&
      (typeof request.periodUnit !== 'string' || !Object.hasOwn(recurringIntervals, request.periodUnit))
    ) {
      return null;
    }
    if (
      request.periodLength !== undefined &&
      (typeof request.periodLength !== 'number' || !Number.isInteger(request.periodLength) || request.periodLength <= 0)
    ) {
      return null;
    }
    return request as unknown as AdyenRecurringPaymentRequestModel;
  } catch {
    return null;
  }
}

export function appleRecurringPaymentRequest(
  request: AdyenRecurringPaymentRequestModel,
  genericDescription: string
): ApplePayConfiguration['recurringPaymentRequest'] {
  const fractionDigits = new Intl.NumberFormat('en-US', { style: 'currency', currency: request.currency }).resolvedOptions()
    .maximumFractionDigits;
  const amount = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    useGrouping: false,
  }).format(request.amount);
  const interval = request.periodUnit ? recurringIntervals[request.periodUnit] : undefined;
  return {
    paymentDescription: request.planName ?? genericDescription,
    managementURL: `${window.location.origin}/profile?selectedTab=my-billing-info`,
    regularBilling: {
      label: genericDescription,
      amount,
      paymentTiming: 'recurring',
      ...(request.startDate ? { recurringPaymentStartDate: new Date(`${request.startDate}T00:00:00`) } : {}),
      ...(interval
        ? { recurringPaymentIntervalUnit: interval.unit, recurringPaymentIntervalCount: interval.count * (request.periodLength ?? 1) }
        : {}),
    },
  };
}
